import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  listContacts,
  listFolders,
  listTags,
  createContact,
  updateContact,
  deleteContact,
  reverseGeocode,
  listPois,
  listPoiFolders,
  listPoiTags,
  updatePoi,
  listBookings,
  searchAi,
} from "@/lib/api";
import MapView from "@/components/MapView";
import SearchBar from "@/components/SearchBar";
import MediaViewer from "@/components/MediaViewer";
import ContactProfile from "@/components/ContactProfile";
import ContactDialog from "@/components/ContactDialog";
import ContactsListPanel from "@/components/dashboard/ContactsListPanel";
import AiSearchResults from "@/components/AiSearchResults";
import AskContactsAiDrawer from "@/components/AskContactsAiDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FolderIcon, DEFAULT_TAG_COLOR } from "@/lib/folderStyle";
import { buildFolderTree } from "@/components/TagPicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  MapPin,
  PanelLeftClose,
  PanelLeftOpen,
  X,
  Phone,
  Mail,
  StickyNote,
  List,
  Library,
  Tag as TagIcon,
  Filter,
  ChevronRight,
  CalendarDays,
  Repeat,
  LogOut,
  Users,
  Map as MapIcon,
  Search,
  Layers,
  Sparkles,
  Loader2,
} from "lucide-react";

export default function MapPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [contacts, setContacts] = useState([]);

  // POI library (separate; toggled off by default)
  const [poiFolders, setPoiFolders] = useState([]);
  const [poiTags, setPoiTags] = useState([]);
  const [pois, setPois] = useState([]);
  const [showPois, setShowPois] = useState(false);
  const [selectedPoi, setSelectedPoi] = useState(null);

  // active filter: per-folder set of selected tag ids.
  // A contact is visible if for every folder that has selected tags, the contact has at least one of those tags.
  const [selectedTagIds, setSelectedTagIds] = useState(new Set());
  // Per-folder filter combinator. Between folders we always AND; WITHIN a
  // folder users can pick AND (must have every selected tag) or OR (default,
  // any selected tag). Keyed by folder id.
  const [folderFilterModes, setFolderFilterModes] = useState({});
  const folderModeOf = (fid) => folderFilterModes[fid] || "or";
  const setFolderMode = (fid, mode) =>
    setFolderFilterModes((p) => ({ ...p, [fid]: mode }));

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [viewerStart, setViewerStart] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Dashboard: which right-pane view is active. Persists across filter changes.
  const [viewMode, setViewMode] = useState("map"); // "map" | "contacts"
  // Search filter (applies to visibleContacts by name/town/postcode)
  const [searchQuery, setSearchQuery] = useState("");

  // ---- AI natural-language search ----
  // Auto-detect when the query looks like a natural-language question. Short
  // one-word queries stay instant/local; longer or NL-shaped queries auto-run
  // AI search when the user presses Enter. There is also a manual "Ask AI"
  // sparkle button so users can force AI on a short query.
  const [aiSearching, setAiSearching] = useState(false);
  const [aiResults, setAiResults] = useState(null); // { results, applied_filters, degraded }
  const [aiMapFilterIds, setAiMapFilterIds] = useState(null); // Set<string> | null
  const [fitAllVersion, setFitAllVersion] = useState(0);
  const showResultsOnMap = (ids) => {
    setViewMode("map");
    setAiMapFilterIds(new Set(ids));
    setFitAllVersion((v) => v + 1); // explicit user request → auto-fit
  };
  // Conversational drawer state (independent from one-shot AI search above)
  const [askAiOpen, setAskAiOpen] = useState(false);
  // Contact edit/create dialog (inline, so we never leave the Dashboard shell)
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [pendingDeleteContact, setPendingDeleteContact] = useState(null);

  // Bookings (across all contacts)
  const [bookings, setBookings] = useState([]);
  // "Repeat" pseudo-filter — when ON, only contacts with a repeat=true booking
  const [repeatFilter, setRepeatFilter] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [f, t, c, pf, pt, p, b] = await Promise.all([
          listFolders(),
          listTags(),
          listContacts(),
          listPoiFolders(),
          listPoiTags(),
          listPois(),
          listBookings(),
        ]);
        setFolders(f);
        setTags(t);
        setContacts(c);
        setPoiFolders(pf);
        setPoiTags(pt);
        setPois(p);
        setBookings(b);
      } catch {
        toast.error("Failed to load");
      }
    })();
  }, []);

  // Reload bookings when the profile dialog closes (to pick up new edits)
  useEffect(() => {
    if (selectedContact || selectedPoi) return;
    listBookings().then(setBookings).catch(() => {});
  }, [selectedContact, selectedPoi]);

  // Contact IDs that currently have a "repeat" booking → used by the Repeat filter
  const repeatContactIds = useMemo(() => {
    const s = new Set();
    for (const b of bookings) {
      if (b.repeat && b.kind !== "poi") s.add(b.contact_id);
    }
    return s;
  }, [bookings]);

  // Upcoming bookings (not completed, scheduled in future)
  const upcomingBookings = useMemo(() => {
    const now = Date.now();
    return bookings
      .filter((b) => !b.completed && b.scheduled_at && new Date(b.scheduled_at).getTime() >= now)
      .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())
      .slice(0, 5);
  }, [bookings]);

  // Set of contact IDs with ANY upcoming booking → drives the shimmer-star map pin
  const upcomingContactIds = useMemo(() => {
    const now = Date.now();
    const s = new Set();
    for (const b of bookings) {
      if (b.completed) continue;
      if (b.kind === "poi") continue;
      if (!b.scheduled_at) continue;
      if (new Date(b.scheduled_at).getTime() >= now) s.add(b.contact_id);
    }
    return s;
  }, [bookings]);

  const contactById = useMemo(() => {
    const m = new Map();
    contacts.forEach((c) => m.set(c.id, c));
    return m;
  }, [contacts]);

  // Focus a contact when arriving from Roster via /?focus=<id>
  useEffect(() => {
    const fid = searchParams.get("focus");
    if (!fid || contacts.length === 0) return;
    const c = contacts.find((x) => x.id === fid);
    if (c) {
      setSelectedContact(c);
      setFlyTarget({ lat: c.lat, lng: c.lng, zoom: 15 });
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, searchParams]);

  const tagById = useMemo(() => {
    const m = new Map();
    tags.forEach((t) => m.set(t.id, t));
    return m;
  }, [tags]);

  const folderById = useMemo(() => {
    const m = new Map();
    folders.forEach((f) => m.set(f.id, f));
    return m;
  }, [folders]);

  // Identify which folder represents Gender (case-insensitive name match) and
  // map each gender tag to "male" | "female" by name keywords.
  const tagGender = useMemo(() => {
    const m = new Map();
    const genderFolder = folders.find((f) =>
      /^\s*(gender|sex)\s*$/i.test(f.name || "")
    );
    if (!genderFolder) return m;
    tags
      .filter((t) => t.folder_id === genderFolder.id)
      .forEach((t) => {
        const n = (t.name || "").toLowerCase();
        if (/(femal|wom[ae]n|girl|lady|ladies|^f$|she\/her)/.test(n)) {
          m.set(t.id, "female");
        } else if (/(\bmale\b|^m$|\bman\b|\bmen\b|guy|boy|he\/him)/.test(n)) {
          m.set(t.id, "male");
        }
      });
    return m;
  }, [folders, tags]);

  // Resolve each contact's gender from their tags (first match wins).
  // Also remember WHICH tag (so we can use that tag's colour for the pin).
  const contactGenderTag = useMemo(() => {
    const m = new Map();
    contacts.forEach((c) => {
      for (const tid of c.tag_ids || []) {
        const g = tagGender.get(tid);
        if (g) {
          m.set(c.id, { gender: g, tag: tagById.get(tid) });
          break;
        }
      }
    });
    return m;
  }, [contacts, tagGender, tagById]);

  const contactGender = useMemo(() => {
    const m = new Map();
    contactGenderTag.forEach((v, k) => m.set(k, v.gender));
    return m;
  }, [contactGenderTag]);

  // contact -> color. Gender tag colour wins so female contacts are pink,
  // males are their assigned colour, otherwise fall back to first tag colour.
  const contactColors = useMemo(() => {
    const m = new Map();
    contacts.forEach((c) => {
      const gt = contactGenderTag.get(c.id);
      if (gt && gt.tag?.color) {
        m.set(c.id, gt.tag.color);
        return;
      }
      const firstTagId = (c.tag_ids || [])[0];
      const tag = firstTagId ? tagById.get(firstTagId) : null;
      m.set(c.id, tag?.color || DEFAULT_TAG_COLOR);
    });
    return m;
  }, [contacts, tagById, contactGenderTag]);

  // Meeting-folder badges (e.g. Incall / Outcall) per contact — shown on hover
  const meetingBadgesByContact = useMemo(() => {
    const folder = folders.find(
      (f) => (f.name || "").trim().toLowerCase() === "meeting"
    );
    const m = new Map();
    if (!folder) return m;
    contacts.forEach((c) => {
      const badges = (c.tag_ids || [])
        .map((tid) => tagById.get(tid))
        .filter((t) => t && t.folder_id === folder.id)
        .map((t) => ({ name: t.name, color: t.color || DEFAULT_TAG_COLOR }));
      if (badges.length) m.set(c.id, badges);
    });
    return m;
  }, [contacts, folders, tagById]);

  // Age tag (from a folder literally named "Age") per contact — used on hover
  const ageByContact = useMemo(() => {
    const folder = folders.find(
      (f) => (f.name || "").trim().toLowerCase() === "age"
    );
    const m = new Map();
    if (!folder) return m;
    contacts.forEach((c) => {
      const t = (c.tag_ids || [])
        .map((tid) => tagById.get(tid))
        .find((t) => t && t.folder_id === folder.id);
      if (t) m.set(c.id, t.name);
    });
    return m;
  }, [contacts, folders, tagById]);

  // Set of contact IDs that have a "Yes" tag under a "Will Video"-ish folder
  const videoContactIds = useMemo(() => {
    const folder = folders.find((f) =>
      (f.name || "").toLowerCase().includes("video")
    );
    const s = new Set();
    if (!folder) return s;
    contacts.forEach((c) => {
      const has = (c.tag_ids || [])
        .map((tid) => tagById.get(tid))
        .some(
          (t) =>
            t &&
            t.folder_id === folder.id &&
            /yes|will/i.test(t.name || "")
        );
      if (has) s.add(c.id);
    });
    return s;
  }, [contacts, folders, tagById]);

  // POI colour map (first tag's colour, or default teal)
  const poiTagById = useMemo(() => {
    const m = new Map();
    poiTags.forEach((t) => m.set(t.id, t));
    return m;
  }, [poiTags]);

  const poiColors = useMemo(() => {
    const m = new Map();
    pois.forEach((p) => {
      const firstTagId = (p.tag_ids || [])[0];
      const tag = firstTagId ? poiTagById.get(firstTagId) : null;
      m.set(p.id, tag?.color || "#00d4a0");
    });
    return m;
  }, [pois, poiTagById]);

  // Filter contacts by selected tags.
  // - Between folders → always AND (a contact must satisfy every folder constraint).
  // - Within a folder → mode is per-folder: "or" (default, any selected tag) or
  //   "and" (must have every selected tag from that folder).
  // - Repeat filter: when ON, only contacts with a repeat=true booking are visible.
  // - Search: applies free-text filter across name/town/postcode.
  //
  // manuallyFilteredContactIds mirrors this logic MINUS the free-text search box,
  // so it can be handed to /api/search/ai as the "pre-filtered set" (existing
  // manual filters must always compose with AI search).
  const manuallyFilteredContactIds = useMemo(() => {
    let base = contacts;
    if (repeatFilter) {
      base = base.filter((c) => repeatContactIds.has(c.id));
    }
    if (selectedTagIds.size > 0) {
      const byFolder = new Map();
      selectedTagIds.forEach((tid) => {
        const t = tagById.get(tid);
        if (!t) return;
        const arr = byFolder.get(t.folder_id) || [];
        arr.push(tid);
        byFolder.set(t.folder_id, arr);
      });
      base = base.filter((c) => {
        const ctags = new Set(c.tag_ids || []);
        for (const [fid, tids] of byFolder) {
          const mode = folderModeOf(fid);
          const ok =
            mode === "and"
              ? tids.every((tid) => ctags.has(tid))
              : tids.some((tid) => ctags.has(tid));
          if (!ok) return false;
        }
        return true;
      });
    }
    return base.map((c) => c.id);
  }, [contacts, selectedTagIds, tagById, folderFilterModes, repeatFilter, repeatContactIds]);

  const visibleContacts = useMemo(() => {
    let base = contacts;
    if (repeatFilter) {
      base = base.filter((c) => repeatContactIds.has(c.id));
    }
    const q = (searchQuery || "").trim().toLowerCase();
    if (q && !aiResults) {
      base = base.filter((c) => {
        const hay = [c.name, c.town, c.region, c.postcode, c.address, c.email]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    if (selectedTagIds.size > 0) {
      const byFolder = new Map();
      selectedTagIds.forEach((tid) => {
        const t = tagById.get(tid);
        if (!t) return;
        const arr = byFolder.get(t.folder_id) || [];
        arr.push(tid);
        byFolder.set(t.folder_id, arr);
      });
      base = base.filter((c) => {
        const ctags = new Set(c.tag_ids || []);
        for (const [fid, tids] of byFolder) {
          const mode = folderModeOf(fid);
          const ok =
            mode === "and"
              ? tids.every((tid) => ctags.has(tid))
              : tids.some((tid) => ctags.has(tid));
          if (!ok) return false;
        }
        return true;
      });
    }
    // "Show N AI results on map" — temporarily narrow to just those ids while
    // the underlying manual filters stay intact underneath.
    if (aiMapFilterIds) {
      const s = aiMapFilterIds;
      base = base.filter((c) => s.has(c.id));
    }
    return base;
  }, [contacts, selectedTagIds, tagById, folderFilterModes, repeatFilter, repeatContactIds, searchQuery, aiResults, aiMapFilterIds]);

  const toggleTag = (tagId) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  };

  const clearFilter = () => {
    setSelectedTagIds(new Set());
    setRepeatFilter(false);
  };

  // ---- AI search helpers ----
  const contactsById = useMemo(() => {
    const m = new Map();
    for (const c of contacts) m.set(c.id, c);
    return m;
  }, [contacts]);

  // Detect NL-shaped queries so pressing Enter auto-routes to AI.
  const looksLikeNaturalLanguage = (q) => {
    const s = (q || "").trim();
    if (!s) return false;
    if (s.length < 8) return false;
    const words = s.split(/\s+/).length;
    if (words >= 4) return true;
    return /\b(who|whom|find|show|near|in|from|which|what|when|why|haven'?t|have|does|do|will|rated|with|mention|like|prefer)\b/i.test(s);
  };

  // Run one AI search. Respects currently selected tag filters / repeat filter
  // by pre-filtering the contact set on the client and sending its ids.
  const runAiSearch = async (q) => {
    const query = (q ?? searchQuery).trim();
    if (!query) return;
    setAiSearching(true);
    try {
      const preIds = manuallyFilteredContactIds; // computed below via useMemo
      const data = await searchAi(query, preIds);
      setAiResults({
        results: data.results || [],
        applied_filters: data.applied_filters || [],
        degraded: !!data.degraded,
      });
    } catch (e) {
      setAiResults({ results: [], applied_filters: [], degraded: true });
    } finally {
      setAiSearching(false);
    }
  };

  const clearAiSearch = () => {
    setAiResults(null);
    setAiMapFilterIds(null);
  };

  const handleSelectPlace = (place) => {
    setFlyTarget({ lat: place.lat, lng: place.lng, zoom: 13 });
  };

  // Drag a pin to reposition it
  const handleMarkerDragEnd = async (contact, { lat, lng }) => {
    try {
      const info = await reverseGeocode(lat, lng);
      const payload = { lat, lng };
      if (info) {
        payload.location_name = info.short || info.town || null;
        payload.street = info.street || null;
        payload.town = info.town || null;
        payload.region = info.region || null;
        payload.postcode = info.postcode || null;
        payload.country = info.country || null;
      }
      const updated = await updateContact(contact.id, payload);
      setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      if (selectedContact?.id === updated.id) setSelectedContact(updated);
      toast.success(info?.town ? `Moved to ${info.town}` : "Pin moved");
    } catch {
      toast.error("Could not move pin");
    }
  };

  const handlePoiDragEnd = async (poi, { lat, lng }) => {
    try {
      const info = await reverseGeocode(lat, lng);
      const payload = { lat, lng };
      if (info) {
        payload.location_name = info.short || info.town || null;
        payload.street = info.street || null;
        payload.town = info.town || null;
        payload.region = info.region || null;
        payload.postcode = info.postcode || null;
        payload.country = info.country || null;
      }
      const updated = await updatePoi(poi.id, payload);
      setPois((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      if (selectedPoi?.id === updated.id) setSelectedPoi(updated);
      toast.success(info?.town ? `POI moved to ${info.town}` : "POI moved");
    } catch {
      toast.error("Could not move POI");
    }
  };

  // Right-click / long-press on the map → choose Contact or POI, then jump
  // to the Roster with the address fields pre-filled via reverse-geocode.
  const handleAddAtPoint = async (kind) => {
    if (!contextMenu) return;
    const { lat, lng } = contextMenu;
    setContextMenu(null);
    const params = new URLSearchParams({
      add: "1",
      lat: String(lat),
      lng: String(lng),
    });
    if (kind === "poi") params.set("kind", "poi");
    try {
      const info = await reverseGeocode(lat, lng);
      if (info) {
        if (info.short) params.set("loc", info.short);
        if (info.street) params.set("street", info.street);
        if (info.town) params.set("town", info.town);
        if (info.region) params.set("region", info.region);
        if (info.postcode) params.set("postcode", info.postcode);
        if (info.country) params.set("country", info.country);
      }
    } catch {
      /* keep going with just lat/lng */
    }
    navigate(`/roster?${params.toString()}`);
  };

  // Tags grouped by folder for the filter sidebar
  const tagsByFolder = useMemo(() => {
    const m = new Map();
    tags.forEach((t) => {
      const arr = m.get(t.folder_id) || [];
      arr.push(t);
      m.set(t.folder_id, arr);
    });
    m.forEach((arr) =>
      arr.sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)
      )
    );
    return m;
  }, [tags]);

  const tree = useMemo(() => buildFolderTree(folders), [folders]);

  // Collapsed-by-default folder expand state for the filter sidebar
  const [expandedFolders, setExpandedFolders] = useState({});
  const isFolderOpen = (fid) => expandedFolders[fid] ?? false;
  const toggleFolder = (fid) =>
    setExpandedFolders((p) => ({ ...p, [fid]: !isFolderOpen(fid) }));

  const renderFolderFilter = (node, depth) => {
    const ts = tagsByFolder.get(node.id) || [];
    const open = isFolderOpen(node.id);
    const selectedInside = ts.filter((t) => selectedTagIds.has(t.id)).length;
    return (
      <div key={node.id} className="space-y-1.5" style={{ paddingLeft: depth * 10 }}>
        <button
          type="button"
          onClick={() => toggleFolder(node.id)}
          className="w-full flex items-center gap-2 text-left"
          aria-expanded={open}
          data-testid={`map-folder-toggle-${node.id}`}
        >
          <ChevronRight
            className={`h-3.5 w-3.5 text-white/45 transition-transform ${
              open ? "rotate-90" : ""
            }`}
          />
          <span className="inline-flex items-center gap-1.5" style={{ color: node.color }}>
            <FolderIcon name={node.icon || "Folder"} className="h-3.5 w-3.5" />
            <span className="text-[11px] uppercase tracking-widest font-mono">{node.name}</span>
          </span>
          {ts.length > 0 && (
            <span className="text-[9px] uppercase tracking-widest font-mono text-white/35">
              · {ts.length}
            </span>
          )}
          {selectedInside > 0 && !open && (
            <span
              className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-full ml-auto"
              style={{
                background: `${node.color}33`,
                color: node.color,
                border: `1px solid ${node.color}AA`,
              }}
            >
              {selectedInside}
            </span>
          )}
        </button>
        {open &&
          (ts.length > 0 ? (
            <div className="space-y-1.5" style={{ paddingLeft: 18 }}>
              {selectedInside >= 2 && (
                <div
                  className="inline-flex items-center rounded-full border border-white/10 bg-white/5 p-0.5 mr-2"
                  data-testid={`map-folder-mode-${node.id}`}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFolderMode(node.id, "or");
                    }}
                    className={`h-6 px-2.5 rounded-full font-mono uppercase tracking-widest text-[9px] transition-colors ${
                      folderModeOf(node.id) === "or"
                        ? "bg-[#f5d300]/15 text-[#f5d300] border border-[#f5d300]/50"
                        : "text-white/55 hover:text-white"
                    }`}
                    title={`Match any selected ${node.name} tag`}
                    data-testid={`map-folder-mode-or-${node.id}`}
                  >
                    Or
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFolderMode(node.id, "and");
                    }}
                    className={`h-6 px-2.5 rounded-full font-mono uppercase tracking-widest text-[9px] transition-colors ${
                      folderModeOf(node.id) === "and"
                        ? "bg-[#00e5ff]/15 text-[#00e5ff] border border-[#00e5ff]/50"
                        : "text-white/55 hover:text-white"
                    }`}
                    title={`Must have every selected ${node.name} tag`}
                    data-testid={`map-folder-mode-and-${node.id}`}
                  >
                    And
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {ts.map((t) => {
                  const sel = selectedTagIds.has(t.id);
                  const color = t.color || node.color || DEFAULT_TAG_COLOR;
                  return (
                    <button
                      key={t.id}
                      onClick={() => toggleTag(t.id)}
                      className="inline-flex items-center gap-1 rounded-full px-2.5 h-7 text-[11px] font-mono uppercase tracking-widest transition-all"
                      style={
                        sel
                          ? {
                              background: color,
                              color: "#0a0a0a",
                              border: `1px solid ${color}`,
                              boxShadow: `0 0 10px ${color}AA`,
                            }
                          : {
                              background: "transparent",
                              color,
                              border: `1px solid ${color}66`,
                            }
                      }
                      data-testid={`map-tag-filter-${t.id}`}
                    >
                      <TagIcon className="h-2.5 w-2.5" />
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div
              className="text-[10px] font-mono italic text-white/30"
              style={{ paddingLeft: 18 }}
            >
              no tags
            </div>
          ))}
        {node.children.map((c) => renderFolderFilter(c, depth + 1))}
      </div>
    );
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[#0a0a0a] text-white">
      <div className="absolute inset-0 z-0" style={{ visibility: viewMode === "map" ? "visible" : "hidden" }}>
        <MapView
          contacts={visibleContacts}
          contactColors={contactColors}
          contactGender={contactGender}
          meetingBadgesById={meetingBadgesByContact}
          ageByContact={ageByContact}
          videoContactIds={videoContactIds}
          pois={pois}
          poiColors={poiColors}
          showPois={showPois}
          tempPin={null}
          flyTarget={flyTarget}
          onFlyDone={() => setFlyTarget(null)}
          fitAllVersion={fitAllVersion}
          selectedId={selectedContact?.id}
          onMapClick={null}
          onMarkerClick={(c) => {
            setSelectedContact(c);
            setFlyTarget({ lat: c.lat, lng: c.lng, zoom: 14 });
          }}
          onMarkerDragEnd={handleMarkerDragEnd}
          onPoiClick={(p) => {
            setSelectedPoi(p);
            setFlyTarget({ lat: p.lat, lng: p.lng, zoom: 14 });
          }}
          onPoiDragEnd={handlePoiDragEnd}
          onContextMenu={(info) => setContextMenu(info)}
          upcomingContactIds={upcomingContactIds}
        />
        <div className="map-grain" />
      </div>

      {/* Contacts view (right-pane overlay). Uses same filter state as Map. */}
      {viewMode === "contacts" && (
        <div
          className={`absolute z-10 bg-[#0a0a0a] overflow-hidden transition-[left] duration-300 top-0 right-0 bottom-0 left-0 ${
            sidebarOpen ? "md:!left-[26rem]" : "md:!left-0"
          }`}
          style={{ paddingTop: 96 }}
          data-testid="contacts-overlay"
        >
          <ContactsListPanel
            contacts={visibleContacts}
            totalCount={contacts.length}
            tagById={tagById}
            contactColors={contactColors}
            meetingBadgesById={meetingBadgesByContact}
            upcomingContactIds={upcomingContactIds}
            onSelect={(c) => {
              setSelectedContact(c);
            }}
            onEdit={(c) => {
              setEditingContact(c);
              setContactDialogOpen(true);
            }}
            onDelete={(c) => setPendingDeleteContact(c)}
            onCreateNew={() => {
              setEditingContact(null);
              setContactDialogOpen(true);
            }}
          />
        </div>
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-40 pointer-events-none">
        <div className="flex items-start justify-between p-4 md:p-6 gap-4">
          {/* Brand + Search — hide together with the filter sidebar */}
          <div
            className={`flex-1 flex items-start gap-4 transition-all duration-300 ${
              sidebarOpen
                ? "opacity-100 -translate-y-0"
                : "opacity-0 -translate-y-6 pointer-events-none"
            }`}
            aria-hidden={!sidebarOpen}
          >
            {/* Brand badge removed */}

            <div className="flex-1 flex justify-center pointer-events-none">
              <SearchBar
              onSelectPlace={handleSelectPlace}
              contacts={contacts}
              onSelectContact={(c) => {
                setSelectedContact(c);
                setFlyTarget({ lat: c.lat, lng: c.lng, zoom: 14 });
              }}
            />
            </div>
          </div>

          <div className="pointer-events-auto flex items-center gap-2 shrink-0">
            {/* View toggle — Contacts | Map (persistent, shares filter state) */}
            <div
              className="hidden md:inline-flex items-center h-14 p-1 rounded-2xl border border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl"
              data-testid="view-toggle"
            >
              <button
                type="button"
                onClick={() => setViewMode("contacts")}
                className={`inline-flex items-center gap-2 h-12 px-4 rounded-xl font-display uppercase tracking-wider text-sm transition-colors ${
                  viewMode === "contacts"
                    ? "bg-[#00e5ff]/15 text-[#00e5ff] shadow-[0_0_16px_rgba(0,229,255,0.25)_inset]"
                    : "text-white/60 hover:text-white"
                }`}
                data-testid="view-toggle-contacts"
              >
                <Users className="h-4 w-4" /> Contacts
              </button>
              <button
                type="button"
                onClick={() => setViewMode("map")}
                className={`inline-flex items-center gap-2 h-12 px-4 rounded-xl font-display uppercase tracking-wider text-sm transition-colors ${
                  viewMode === "map"
                    ? "bg-[#00e5ff]/15 text-[#00e5ff] shadow-[0_0_16px_rgba(0,229,255,0.25)_inset]"
                    : "text-white/60 hover:text-white"
                }`}
                data-testid="view-toggle-map"
              >
                <MapIcon className="h-4 w-4" /> Map
              </button>
            </div>
            {/* Mobile: single icon that flips between the two */}
            <button
              type="button"
              onClick={() => setViewMode((v) => (v === "map" ? "contacts" : "map"))}
              className="md:hidden h-14 w-14 rounded-2xl border border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl flex items-center justify-center text-[#00e5ff] hover:border-[#00e5ff] transition-colors"
              data-testid="view-toggle-mobile"
              aria-label="Toggle Contacts / Map view"
            >
              {viewMode === "map" ? <Users className="h-5 w-5" /> : <MapIcon className="h-5 w-5" />}
            </button>
            <button
              onClick={() => setAskAiOpen(true)}
              className="h-14 w-14 rounded-2xl border border-[#00e5ff]/40 bg-[#0a0a0a]/80 backdrop-blur-xl flex items-center justify-center text-[#00e5ff] hover:text-white hover:border-[#00e5ff] transition-colors shadow-[0_0_16px_rgba(0,229,255,0.15)]"
              data-testid="ai-drawer-open"
              aria-label="Ask Contacts AI"
              title="Ask Contacts AI"
            >
              <Sparkles className="h-5 w-5" />
            </button>
            <button
              onClick={() => setSidebarOpen((s) => !s)}
              className="h-14 w-14 rounded-2xl border border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl flex items-center justify-center text-white hover:text-[#00e5ff] hover:border-[#00e5ff] transition-colors"
              data-testid="sidebar-toggle"
              aria-label="Toggle filter sidebar"
            >
              {sidebarOpen ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeftOpen className="h-5 w-5" />}
            </button>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="h-14 w-14 rounded-2xl border border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl flex items-center justify-center text-white/70 hover:text-[#ff2a8f] hover:border-[#ff2a8f]/60 transition-colors"
              data-testid="logout-button"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filter sidebar */}
      <aside
        className={`absolute z-40 top-24 bottom-6 left-4 md:left-6 w-[22rem] max-w-[90vw] rounded-2xl border border-white/10 bg-[#0a0a0a]/85 backdrop-blur-2xl flex flex-col transition-all duration-300 cyber-corners ${
          sidebarOpen ? "translate-x-0 opacity-100" : "-translate-x-[120%] opacity-0"
        }`}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="flex items-center gap-2 font-display uppercase text-lg tracking-wider text-white">
              <Filter className="h-4 w-4 text-[#00e5ff]" /> Dashboard
            </div>
            <div className="font-mono text-xs text-white/40 mt-0.5">
              {visibleContacts.length} of {contacts.length} contacts
            </div>
          </div>
          <Button
            onClick={() => navigate("/tags")}
            className="btn-ghost h-10 px-3 text-[#00e5ff] border-[#00e5ff]/40"
            data-testid="open-tag-library-button"
            title="Open Tag Library"
          >
            <Library className="h-4 w-4 mr-1.5" /> Tags
          </Button>
        </div>

        {/* Search across name / town / postcode / email — plus AI natural-language search */}
        <div className="px-5 pt-4 pb-3 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                // Clearing the box also clears any pinned AI result view
                if (!e.target.value) clearAiSearch();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (looksLikeNaturalLanguage(searchQuery)) {
                    runAiSearch();
                  }
                }
              }}
              placeholder="Search or ask… e.g. GILFs in Canada who do incalls"
              className="bg-black/40 border-white/10 text-white h-10 pl-9 pr-20 font-mono text-sm"
              data-testid="sidebar-search"
            />
            <button
              type="button"
              onClick={() => runAiSearch()}
              disabled={aiSearching || !searchQuery.trim()}
              className="absolute right-9 top-1/2 -translate-y-1/2 text-[#00e5ff] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed p-1.5"
              aria-label="Ask AI"
              title="Ask AI ✨"
              data-testid="sidebar-search-ai"
            >
              {aiSearching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
            </button>
            {(searchQuery || aiResults) && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  clearAiSearch();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-1.5"
                aria-label="Clear search"
                data-testid="sidebar-search-clear"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {(selectedTagIds.size > 0 || repeatFilter) && aiResults && (
            <div
              className="mt-2 text-[10px] font-mono uppercase tracking-wider text-white/50"
              data-testid="ai-search-filters-note"
            >
              AI search is scoped to your active dashboard filters
            </div>
          )}
        </div>

        {aiResults && (
          <AiSearchResults
            results={aiResults.results}
            contactsById={contactsById}
            appliedFilters={aiResults.applied_filters}
            degraded={aiResults.degraded}
            onOpen={(c) => {
              setSelectedContact(c);
            }}
            onEdit={(c) => {
              setEditingContact(c);
              setContactDialogOpen(true);
            }}
            onShowOnMap={(c) => {
              setViewMode("map");
              setFlyTarget({ lat: c.lat, lng: c.lng, zoom: 15 });
            }}
            onShowAllOnMap={(ids) => {
              showResultsOnMap(ids);
            }}
            onClose={clearAiSearch}
          />
        )}

        {aiMapFilterIds && (
          <div
            className="mx-5 mt-2 flex items-center justify-between rounded-md border border-[#00e5ff]/40 bg-[#00e5ff]/10 px-3 py-2 text-[11px] font-mono text-[#00e5ff]"
            data-testid="ai-map-filter-banner"
          >
            <span>Showing {aiMapFilterIds.size} AI result{aiMapFilterIds.size === 1 ? "" : "s"} on map</span>
            <button
              type="button"
              onClick={() => setAiMapFilterIds(null)}
              className="uppercase tracking-wider hover:text-white"
              data-testid="ai-map-filter-clear"
            >
              Clear
            </button>
          </div>
        )}

        {/* Layers toggle */}
        <div className="px-5 pt-4 pb-3 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-white/45 font-mono">
              Layers
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-full border border-[#f5d300]/60 bg-[#f5d300]/15 text-[#f5d300] font-mono uppercase tracking-widest text-[11px]"
              data-testid="layer-contacts"
              disabled
              title="Contacts are always on"
            >
              <span className="h-2 w-2 rounded-full bg-[#f5d300] shadow-[0_0_8px_rgba(245,211,0,0.9)]"></span>
              Contacts · {contacts.length}
            </button>
            <button
              type="button"
              onClick={() => setShowPois((s) => !s)}
              className={`flex-1 inline-flex items-center justify-center gap-2 h-9 rounded-full border font-mono uppercase tracking-widest text-[11px] transition-colors ${
                showPois
                  ? "border-[#00d4a0]/70 bg-[#00d4a0]/15 text-[#00d4a0]"
                  : "border-white/15 bg-transparent text-white/55 hover:text-white hover:border-white/30"
              }`}
              data-testid="layer-pois"
              aria-pressed={showPois}
            >
              <span
                className={`h-2 w-2 ${showPois ? "bg-[#00d4a0] shadow-[0_0_8px_rgba(0,212,160,0.9)]" : "bg-white/30"}`}
                style={{ transform: "rotate(45deg)" }}
              ></span>
              Points of Interest · {pois.length}
            </button>
          </div>
          {showPois && pois.length === 0 && (
            <div className="text-[10px] font-mono italic text-white/35 mt-2">
              No points of interest yet — add some from the Roster page.
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll p-4 space-y-3">
          {tree.length === 0 ? (
            <div className="text-center py-10 space-y-3">
              <Library className="h-7 w-7 text-[#00e5ff] mx-auto opacity-70" />
              <div className="font-display uppercase tracking-wider text-white text-sm">
                No tag library yet
              </div>
              <p className="font-mono text-xs text-white/55 leading-relaxed">
                Set up folders (Gender, Age, Attributes…) and the tags inside them. Then come back
                here to filter pins by tag.
              </p>
              <Button
                onClick={() => navigate("/tags")}
                className="btn-neon h-10 px-4"
                data-testid="empty-open-tag-library"
              >
                <Library className="h-4 w-4 mr-1.5" /> Open Tag Library
              </Button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setFilterDialogOpen(true)}
                className={`w-full inline-flex items-center justify-between gap-2 h-12 px-4 rounded-2xl border transition-colors font-display uppercase tracking-wider text-sm ${
                  selectedTagIds.size > 0
                    ? "border-[#00e5ff]/60 bg-[#00e5ff]/10 text-[#00e5ff] hover:bg-[#00e5ff]/20"
                    : "border-white/15 bg-white/[0.02] text-white hover:border-[#00e5ff]/60 hover:text-[#00e5ff]"
                }`}
                data-testid="open-filter-dialog"
              >
                <span className="inline-flex items-center gap-2">
                  <Filter className="h-4 w-4" /> Filter Tags
                </span>
                <span
                  className={`inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full font-mono text-[11px] ${
                    selectedTagIds.size > 0
                      ? "bg-[#00e5ff] text-[#0a0a0a]"
                      : "bg-white/10 text-white/60"
                  }`}
                >
                  {selectedTagIds.size}
                </span>
              </button>

              {/* Repeat filter — toggled from the booking workflow (also standalone here) */}
              <button
                type="button"
                onClick={() => setRepeatFilter((v) => !v)}
                className={`w-full inline-flex items-center justify-between gap-2 h-11 px-4 rounded-2xl border transition-colors font-display uppercase tracking-wider text-xs ${
                  repeatFilter
                    ? "border-[#ff8c00]/70 bg-[#ff8c00]/10 text-[#ff8c00] hover:bg-[#ff8c00]/20"
                    : "border-white/15 bg-white/[0.02] text-white/70 hover:border-[#ff8c00]/60 hover:text-[#ff8c00]"
                }`}
                data-testid="repeat-filter-toggle"
                title="Show only contacts marked as Repeat in their bookings"
              >
                <span className="inline-flex items-center gap-2">
                  <Repeat className="h-4 w-4" /> Repeat only
                </span>
                <span
                  className={`inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full font-mono text-[11px] ${
                    repeatFilter
                      ? "bg-[#ff8c00] text-[#0a0a0a]"
                      : "bg-white/10 text-white/60"
                  }`}
                >
                  {repeatContactIds.size}
                </span>
              </button>

              {/* Compact summary of active filters (so the user knows what's on without opening the dialog) */}
              {selectedTagIds.size > 0 && (
                <div
                  className="flex flex-wrap gap-1.5"
                  data-testid="active-filter-chips"
                >
                  {Array.from(selectedTagIds).slice(0, 8).map((tid) => {
                    const t = tagById.get(tid);
                    if (!t) return null;
                    const color = t.color || DEFAULT_TAG_COLOR;
                    return (
                      <button
                        key={tid}
                        onClick={() => toggleTag(tid)}
                        className="inline-flex items-center gap-1 rounded-full px-2 h-6 text-[10px] font-mono uppercase tracking-widest"
                        style={{
                          background: color,
                          color: "#0a0a0a",
                          boxShadow: `0 0 8px ${color}AA`,
                        }}
                        title="Remove filter"
                        data-testid={`active-filter-${tid}`}
                      >
                        <TagIcon className="h-2.5 w-2.5" />
                        {t.name}
                        <X className="h-2.5 w-2.5 ml-0.5 opacity-70" />
                      </button>
                    );
                  })}
                  {selectedTagIds.size > 8 && (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-white/50 px-1 self-center">
                      +{selectedTagIds.size - 8}
                    </span>
                  )}
                  <button
                    onClick={clearFilter}
                    className="inline-flex items-center gap-1 rounded-full px-2 h-6 text-[10px] font-mono uppercase tracking-widest text-white/55 hover:text-white border border-white/15"
                    data-testid="clear-filter-button"
                  >
                    <X className="h-2.5 w-2.5" /> Clear
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Upcoming bookings (top 5) */}
        {upcomingBookings.length > 0 && (
          <div className="px-4 pb-3 pt-1 border-t border-white/10">
            <div className="upcoming-panel space-y-2">
              <div className="text-[10px] uppercase tracking-[0.22em] font-mono flex items-center gap-1.5 px-1 upcoming-panel-title">
                <CalendarDays className="h-3 w-3" />
                Upcoming bookings
              </div>
              <div className="space-y-1.5">
                {upcomingBookings.map((b) => {
                  const c = contactById.get(b.contact_id);
                  if (!c) return null;
                  const when = new Date(b.scheduled_at);
                  return (
                    <button
                      type="button"
                      key={b.id}
                      onClick={() => {
                        setSelectedContact(c);
                        setFlyTarget({ lat: c.lat, lng: c.lng, zoom: 14 });
                      }}
                      className="upcoming-row w-full text-left"
                      data-testid={`upcoming-${b.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate font-display tracking-wide">
                          {b.title || `Meet ${c.name}`}
                        </div>
                        <div className="when truncate">
                          {when.toLocaleString(undefined, {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {" · "}
                          {c.name}
                        </div>
                      </div>
                      {b.repeat && (
                        <span className="chip chip-repeat shrink-0">
                          <Repeat className="h-3 w-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-t border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/40">
          Tap the Contacts view to add or edit contacts inline
        </div>
      </aside>

      {/* Right-click / long-press popover — appears at the click point on the map */}
      {contextMenu && (
        <>
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => setContextMenu(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setContextMenu(null);
            }}
            data-testid="context-menu-backdrop"
          />
          <div
            className="fixed z-[70] rounded-xl border border-white/10 bg-[#0a0a0a]/97 backdrop-blur-xl shadow-2xl overflow-hidden"
            style={{
              left: Math.min(contextMenu.clientX, window.innerWidth - 230),
              top: Math.min(contextMenu.clientY, window.innerHeight - 130),
              minWidth: 220,
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="map-context-menu"
          >
            <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
              Add at this point
            </div>
            <button
              type="button"
              onClick={() => handleAddAtPoint("contact")}
              className="w-full text-left px-3 py-3 flex items-center gap-3 hover:bg-[#f5d300]/10 text-white hover:text-[#f5d300] transition-colors"
              data-testid="ctx-menu-new-contact"
            >
              <span className="h-3 w-3 rounded-full bg-[#f5d300] shadow-[0_0_8px_rgba(245,211,0,0.9)]"></span>
              <div className="min-w-0">
                <div className="font-display uppercase text-sm tracking-wider">New Contact</div>
                <div className="font-mono text-[10px] text-white/45">Person · phone · profile</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleAddAtPoint("poi")}
              className="w-full text-left px-3 py-3 flex items-center gap-3 hover:bg-[#00d4a0]/10 text-white hover:text-[#00d4a0] transition-colors border-t border-white/5"
              data-testid="ctx-menu-new-poi"
            >
              <span
                className="h-3 w-3 bg-[#00d4a0] shadow-[0_0_8px_rgba(0,212,160,0.9)]"
                style={{ transform: "rotate(45deg)" }}
              ></span>
              <div className="min-w-0">
                <div className="font-display uppercase text-sm tracking-wider">
                  New Point of Interest
                </div>
                <div className="font-mono text-[10px] text-white/45">Place · restaurant · hotel</div>
              </div>
            </button>
          </div>
        </>
      )}

      {/* Right-side Filter Tags drawer — keeps the Dashboard sidebar and the
          active Contacts/Map view visible behind a soft dim so the user can
          scan tags without leaving the workspace. */}
      <Sheet open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md md:max-w-lg lg:max-w-xl bg-[#0a0a0a]/97 backdrop-blur-2xl border-l border-white/10 text-white p-0 flex flex-col [&>button.absolute]:hidden"
          data-testid="filter-drawer"
        >
          <div className="absolute top-3 right-3 z-10">
            <button
              type="button"
              onClick={() => setFilterDialogOpen(false)}
              className="h-10 w-10 rounded-full bg-[#ff5a5a]/15 border border-[#ff5a5a]/40 text-[#ff7a7a] hover:bg-[#ff5a5a]/25 hover:text-[#ff9a9a] flex items-center justify-center transition-colors"
              aria-label="Close filter"
              data-testid="filter-dialog-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <SheetHeader className="px-6 pt-6 pb-3 pr-16 border-b border-white/10 text-left">
            <SheetTitle className="font-display text-2xl uppercase tracking-wider flex items-center gap-3 text-white">
              <Filter className="h-5 w-5 text-[#00e5ff]" />
              Filter Tags
            </SheetTitle>
            <SheetDescription className="font-mono text-xs text-white/50">
              {visibleContacts.length} of {contacts.length} pins visible · between folders is AND ·
              within a folder pick And or Or.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto thin-scroll p-5 space-y-4">
            {tree.map((n) => renderFolderFilter(n, 0))}
          </div>

          <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between gap-3">
            <button
              onClick={clearFilter}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-white/30 font-mono uppercase tracking-widest text-[11px] disabled:opacity-40"
              data-testid="filter-dialog-clear"
              disabled={selectedTagIds.size === 0}
            >
              <X className="h-3.5 w-3.5" /> Clear all
            </button>
            <Button
              onClick={() => setFilterDialogOpen(false)}
              className="btn-neon h-10 px-5"
              data-testid="filter-dialog-done"
            >
              Done · {visibleContacts.length}/{contacts.length} pins
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Centered profile modal */}
      <ContactProfile
        open={!!selectedContact}
        contact={selectedContact}
        tagById={tagById}
        onOpenChange={(o) => !o && setSelectedContact(null)}
        onEdit={() => {
          if (selectedContact) {
            setEditingContact(selectedContact);
            setSelectedContact(null);
            setContactDialogOpen(true);
          }
        }}
        onShowOnMap={() => {
          if (selectedContact) {
            setFlyTarget({ lat: selectedContact.lat, lng: selectedContact.lng, zoom: 15 });
            setSelectedContact(null);
          }
        }}
      />

      {/* Inline Contact create/edit dialog (never leaves the Dashboard shell) */}
      <ContactDialog
        open={contactDialogOpen}
        onOpenChange={(o) => {
          setContactDialogOpen(o);
          if (!o) setEditingContact(null);
        }}
        initialContact={editingContact}
        pin={null}
        folders={folders}
        tags={tags}
        onSave={async (payload) => {
          // Rethrow on failure so ContactDialog can show the correct feedback
          // instead of claiming the save succeeded.
          if (editingContact?.id) {
            const updated = await updateContact(editingContact.id, payload);
            setContacts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            setContactDialogOpen(false);
            setEditingContact(null);
            // Fly the map to the moved pin so the user sees the change land.
            if (Number.isFinite(updated?.lat) && Number.isFinite(updated?.lng)) {
              setViewMode("map");
              setFlyTarget({ lat: updated.lat, lng: updated.lng, zoom: 14 });
            }
            return updated;
          } else {
            const created = await createContact(payload);
            setContacts((prev) => [created, ...prev]);
            setContactDialogOpen(false);
            setEditingContact(null);
            if (Number.isFinite(created?.lat) && Number.isFinite(created?.lng)) {
              setViewMode("map");
              setFlyTarget({ lat: created.lat, lng: created.lng, zoom: 14 });
            }
            return created;
          }
        }}
        onDelete={async () => {
          if (!editingContact?.id) return;
          try {
            await deleteContact(editingContact.id);
            setContacts((prev) => prev.filter((x) => x.id !== editingContact.id));
            toast.success("Contact removed");
            setContactDialogOpen(false);
            setEditingContact(null);
          } catch {
            toast.error("Failed to delete");
          }
        }}
        onOpenTagLibrary={() => navigate("/tags")}
      />

      {/* Quick-delete confirmation (from the Contacts list) */}
      <Dialog
        open={!!pendingDeleteContact}
        onOpenChange={(o) => !o && setPendingDeleteContact(null)}
      >
        <DialogContent className="bg-[#0f0f0f] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide">
              Remove {pendingDeleteContact?.name || "contact"}?
            </DialogTitle>
            <DialogDescription className="text-white/50 text-xs font-mono">
              This deletes the contact and all their bookings. Can't be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => setPendingDeleteContact(null)}
              className="btn-ghost h-11 px-4"
              data-testid="confirm-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const c = pendingDeleteContact;
                if (!c) return;
                try {
                  await deleteContact(c.id);
                  setContacts((prev) => prev.filter((x) => x.id !== c.id));
                  toast.success("Contact removed");
                } catch {
                  toast.error("Failed to delete");
                } finally {
                  setPendingDeleteContact(null);
                }
              }}
              className="h-11 px-4 bg-red-500/85 hover:bg-red-500 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* POI profile modal (same component — POIs share contact-like shape) */}
      <ContactProfile
        open={!!selectedPoi}
        contact={selectedPoi}
        tagById={poiTagById}
        onOpenChange={(o) => !o && setSelectedPoi(null)}
        onEdit={() => selectedPoi && navigate(`/roster?tab=pois&focus=${selectedPoi.id}`)}
        onShowOnMap={() => {
          if (selectedPoi) {
            setFlyTarget({ lat: selectedPoi.lat, lng: selectedPoi.lng, zoom: 15 });
            setSelectedPoi(null);
          }
        }}
      />

      {/* Conversational AI drawer — parallel to the one-shot search box */}
      <AskContactsAiDrawer
        open={askAiOpen}
        onOpenChange={setAskAiOpen}
        contactsById={contactsById}
        manuallyFilteredIds={manuallyFilteredContactIds}
        activeManualFilterLabels={[
          selectedTagIds.size > 0 ? `${selectedTagIds.size} tag${selectedTagIds.size === 1 ? "" : "s"}` : null,
          repeatFilter ? "Repeat only" : null,
        ].filter(Boolean)}
        onOpenContact={(c) => setSelectedContact(c)}
        onEditContact={(c) => {
          setEditingContact(c);
          setContactDialogOpen(true);
          setAskAiOpen(false);
        }}
        onShowContactOnMap={(c) => {
          setViewMode("map");
          setFlyTarget({ lat: c.lat, lng: c.lng, zoom: 15 });
          setAskAiOpen(false);
        }}
        onShowResultsOnMap={(ids) => {
          showResultsOnMap(ids);
          setAskAiOpen(false);
        }}
      />
    </div>
  );
}
